import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, NotebookPen, Search, FilterX } from "lucide-react";
import { toast } from "sonner";
import ExportMenu from "@/components/ExportMenu";
import EmptyState from "@/components/EmptyState";
import AiNoteToolbar from "@/components/AiNoteToolbar";

const TYPE_COLORS = {
  Tenancy: "bg-blue-100 text-blue-700 border-blue-200",
  Property: "bg-amber-100 text-amber-700 border-amber-200",
  General: "bg-slate-100 text-slate-700 border-slate-200",
};

const BLANK = { doc_type: "General", category: "General", text: "", property_id: "", tenant_id: "" };

export default function NotesPage() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [meta, setMeta] = useState({ doc_types: [], categories: [] });
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);

  const [filters, setFilters] = useState({ doc_type: "all", category: "all", date_from: "", date_to: "", q: "" });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (filters.doc_type !== "all") p.doc_type = filters.doc_type;
    if (filters.category !== "all") p.category = filters.category;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.q.trim()) p.q = filters.q.trim();
    return p;
  }, [filters]);

  const load = async () => {
    const { data } = await api.get("/notes", { params });
    setNotes(data);
  };

  useEffect(() => {
    (async () => {
      const [m, p, t] = await Promise.all([
        api.get("/notes/meta"),
        api.get("/properties"),
        api.get("/tenants"),
      ]);
      setMeta(m.data); setProperties(p.data); setTenants(t.data);
    })();
  }, []);

  useEffect(() => { load(); }, [params]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({
      doc_type: n.doc_type,
      category: n.category,
      text: n.text,
      property_id: n.property_id || "",
      tenant_id: n.tenant_id || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) { toast.error("Note text is required"); return; }
    setSaving(true);
    try {
      const payload = {
        doc_type: form.doc_type,
        category: form.category,
        text: form.text.trim(),
        property_id: form.property_id || "",
        tenant_id: form.tenant_id || "",
      };
      if (editing) {
        await api.patch(`/notes/${editing.id}`, payload);
        toast.success("Note updated");
      } else {
        await api.post("/notes", payload);
        toast.success("Note saved");
      }
      setOpen(false); setForm(BLANK); setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/notes/${confirmDelete.id}`);
      toast.success("Note deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const resetFilters = () => setFilters({ doc_type: "all", category: "all", date_from: "", date_to: "", q: "" });
  const hasFilters = filters.doc_type !== "all" || filters.category !== "all" || filters.date_from || filters.date_to || filters.q;

  return (
    <AppLayout
      title="Notes"
      subtitle="Free-form documentation — tenancy, property, and general notes"
      actions={
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/notes/export"
            filenameBase="rojasproperty-notes"
            testidPrefix="export-notes"
            extraParams={params}
            disabled={notes.length === 0}
          />
          <Button data-testid="new-note-button" onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 gap-2">
            <Plus className="h-4 w-4" /> New Note
          </Button>
        </div>
      }
    >
      {/* Filters */}
      <Card className="p-4 mb-5 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Type</Label>
            <Select value={filters.doc_type} onValueChange={(v) => setFilters({ ...filters, doc_type: v })}>
              <SelectTrigger data-testid="notes-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {meta.doc_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Category</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger data-testid="notes-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All categories</SelectItem>
                {meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">From</Label>
            <Input data-testid="notes-filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To</Label>
            <Input data-testid="notes-filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="notes-filter-q"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search note text…"
                className="pl-8"
              />
            </div>
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3 flex justify-end">
            <Button data-testid="notes-clear-filters" variant="ghost" size="sm" onClick={resetFilters} className="gap-1.5 text-slate-500">
              <FilterX className="h-3.5 w-3.5" /> Clear filters
            </Button>
          </div>
        )}
      </Card>

      {/* List */}
      {notes.length === 0 ? (
        <EmptyState
          kind="notes"
          eyebrow={hasFilters ? "No matches" : "A blank notebook"}
          title={hasFilters ? "No notes meet these filters." : "Begin your private archive."}
          body={hasFilters ? "Adjust the type, category or search to find what you're looking for." : "Record observations, contractor notes, applicant impressions or anything worth remembering — all in one elegant place."}
          action={
            <Button data-testid="empty-new-note" onClick={openCreate} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink h-11 px-6 uppercase tracking-[0.2em] text-xs font-semibold transition-colors duration-300">
              <Plus className="h-4 w-4 mr-2" /> Write the first note
            </Button>
          }
        />
      ) : (
        <div className="space-y-3" data-testid="notes-list">
          {notes.map(n => (
            <Card key={n.id} data-testid={`note-${n.id}`} className="p-5 border-slate-200 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  <NotebookPen className="h-5 w-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${TYPE_COLORS[n.doc_type] || TYPE_COLORS.General}`}>{n.doc_type}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-900 text-white">{n.category}</span>
                    {n.property_name && <span className="text-xs text-slate-500">· {n.property_name}</span>}
                    {n.tenant_name && <span className="text-xs text-slate-500">· {n.tenant_name}</span>}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap mb-2">{n.text}</p>
                  <div className="text-xs text-slate-500">
                    {n.author_name} · {(n.created_at || "").slice(0, 16).replace("T", " ")}
                    {n.updated_at && n.updated_at !== n.created_at && (
                      <span className="ml-2 italic">(edited {(n.updated_at || "").slice(0, 16).replace("T", " ")})</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button data-testid={`note-edit-${n.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(n)}>
                    <Pencil className="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button data-testid={`note-delete-${n.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => setConfirmDelete(n)}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(BLANK); } }}>
        <DialogContent className="max-w-xl" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit note" : "New note"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Documentation Type *</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger data-testid="note-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.doc_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="note-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Property (optional)</Label>
                <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="note-property"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tenant (optional)</Label>
                <Select value={form.tenant_id || "none"} onValueChange={(v) => setForm({ ...form, tenant_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="note-tenant"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">—</SelectItem>
                    {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Username</Label>
              <Input value={user?.name || ""} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Note *</Label>
              <Textarea
                data-testid="note-text"
                required
                rows={5}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Write your note here…"
              />
              {(user?.role === "super_admin" || user?.role === "landlord") && (
                <AiNoteToolbar
                  getText={() => form.text}
                  onResult={(text) => setForm((f) => ({ ...f, text }))}
                  disabled={saving}
                />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="note-submit" type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving ? "Saving…" : (editing ? "Save changes" : "Save note")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="note-delete-confirm" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
